import { z } from 'zod';

import { defineQuery } from '@nesso/common-contracts/define';
import { practiceIdSchema, practiceMemberIdSchema } from '@nesso/common-contracts/ids';
import { metadata } from '@nesso/common-contracts/schema-metadata';

import { clientIdSchema, sessionIdSchema } from './ids';
import { sessionSchema } from './session.models';
import {
    generationStatusSchema,
    sessionNoteSourceSchema,
    sessionNoteStatusSchema,
    sessionNoteTypeSchema,
} from './session-note.models';

export const GET_SESSION_BY_ID_TYPE = 'session/get-by-id';
export const GET_SESSION_LIST_TYPE = 'session/get-list';
export const GET_CLIENT_ACTIVITY_TYPE = 'session/get-client-activity';
export const GET_CLIENT_SERVICE_CODES_TYPE = 'session/get-client-service-codes';
export const GET_SCHEDULED_SESSIONS_TYPE = 'session/get-scheduled';

export const getSessionById = defineQuery(
    GET_SESSION_BY_ID_TYPE,
    z.object({
        id: sessionIdSchema,
        practiceMemberId: practiceMemberIdSchema,
    }),
    sessionSchema,
);

export const sessionListItemSchema = sessionSchema
    .pick({
        id: true,
        date: true,
        duration: true,
        clients: true,
        actions: true,
        treatmentApproaches: true,
    })
    .extend({
        isDayCompleted: z.boolean(),
        currentNote: z
            .object({
                id: z.uuid(),
                type: sessionNoteTypeSchema,
                status: sessionNoteStatusSchema,
                source: sessionNoteSourceSchema,
                generator: z
                    .object({
                        status: generationStatusSchema,
                        errorType: z.string().nullable(),
                    })
                    .nullable(),
            })
            .nullable(),
    });

export type SessionListItem = z.infer<typeof sessionListItemSchema>;

export type SessionListItemRecord = z.input<typeof sessionListItemSchema>;

export const sortOrders = ['asc', 'desc'] as const;

export const getSessionList = defineQuery(
    GET_SESSION_LIST_TYPE,
    z.object({
        practiceId: practiceIdSchema,
        practiceMemberId: practiceMemberIdSchema,
        startDate: z.iso.datetime().optional(),
        endDate: z.iso.datetime().optional(),
        status: sessionNoteStatusSchema.optional(),
        clientId: clientIdSchema.optional(),
        order: z.enum(sortOrders).default('desc'),
        search: z.string().trim().optional(),
        excludeFirstGeneration: z.boolean().optional().default(false),
        pageSize: z.number().optional(),
        cursor: z.string().nullable().optional(),
    }),
    z.object({
        items: z.array(sessionListItemSchema),
        nextCursor: z.string().nullable(),
    }),
);

export const clientActivityItemSchema = sessionSchema
    .pick({ id: true, date: true, duration: true, status: true })
    .extend({ hasNote: z.boolean() });

export type ClientActivityItem = z.infer<typeof clientActivityItemSchema>;

export type ClientActivityItemRecord = z.input<typeof clientActivityItemSchema>;

export const getClientActivity = defineQuery(
    GET_CLIENT_ACTIVITY_TYPE,
    z.object({
        clientId: clientIdSchema.meta(metadata.recordId),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        completedCount: z.number().int(),
        cancelledCount: z.number().int(),
        recent: z.array(clientActivityItemSchema),
        nextScheduledDate: z.iso.datetime().nullable(),
    }),
);

export const getClientServiceCodes = defineQuery(
    GET_CLIENT_SERVICE_CODES_TYPE,
    z.object({
        clientId: clientIdSchema.meta(metadata.recordId),
        practiceId: practiceIdSchema.meta(metadata.nonSensitive),
        practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    }),
    z.object({
        serviceCodes: z.array(z.string()),
    }),
);

export const scheduledSessionSchema = z.object({
    id: z.uuid(),
    date: z.iso.datetime(),
});

export type ScheduledSession = z.infer<typeof scheduledSessionSchema>;

export type ScheduledSessionRecord = z.input<typeof scheduledSessionSchema>;

export const getScheduledSessionsInputObjectSchema = z.object({
    practiceId: practiceIdSchema.meta(metadata.nonSensitive),
    practiceMemberId: practiceMemberIdSchema.meta(metadata.nonSensitive),
    startDate: z.iso.datetime().meta(metadata.nonSensitive),
    endDate: z.iso.datetime().meta(metadata.nonSensitive),
});

// The longest range a month grid shows.
const SIX_WEEKS_MS = 6 * 7 * 24 * 60 * 60 * 1000;
// A grid spanning a fall-back clock change is an hour longer in absolute time.
const DST_FALLBACK_ALLOWANCE_MS = 60 * 60 * 1000;
const MAX_RANGE_MS = SIX_WEEKS_MS + DST_FALLBACK_ALLOWANCE_MS;

export const getScheduledSessions = defineQuery(
    GET_SCHEDULED_SESSIONS_TYPE,
    getScheduledSessionsInputObjectSchema.superRefine(
        (value, ctx) => {
            const startMs = new Date(value.startDate).getTime();
            const endMs = new Date(value.endDate).getTime();

            if (endMs <= startMs) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['endDate'],
                    message: 'End date must be after start date',
                });
                return;
            }

            if (endMs - startMs > MAX_RANGE_MS) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['endDate'],
                    message: 'The range cannot span more than six weeks',
                });
            }
        },
        {
            // Skip when a bound already failed its own check, rather than compare an Invalid Date.
            when: (payload) =>
                !payload.issues.some(
                    (issue) => issue.path?.[0] === 'startDate' || issue.path?.[0] === 'endDate',
                ),
        },
    ),
    z.object({
        sessions: z.array(scheduledSessionSchema),
    }),
);
